import { useNavigate } from 'react-router-dom'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Button, Select, Table, Tag, Tooltip } from 'antd'
// eslint-disable-next-line import/no-extraneous-dependencies
import type { ColumnsType } from 'antd/es/table'
import { ToastContainer } from 'react-toastify'
import { ContainerTitle, DivTitle } from '../../components/TitlePage/TitlePage'
import { Loading } from '../../components/Loading/Loading'
import { colors } from '../../theme'
import { useCentralResultados } from './useCentralResultados'
import {
  AVISO_CAUSALIDADE,
  AVISO_INDETERMINADO,
  AVISO_RECEITA,
  AVISO_VALOR_MEDIO,
  FASES,
  SEM_DADO,
  competenciasDisponiveis,
  formatCompetencia,
  formatDataHora,
  formatDecimal,
  formatMoeda,
  formatNumero,
  formatPercentual,
  formatVariacao,
  motivoSemPositivacao,
  rotuloCobertura,
  rotuloFase,
  rotuloSemPositivacao,
} from './centralResultados.helpers'
import {
  AvisoBloco,
  AvisoCausalidade,
  Badge,
  Carimbo,
  Container,
  DeltaHint,
  FilterBar,
  FilterField,
  HeaderLinha,
  LinhaBotoes,
  MetricFracao,
  PainelConvencoes,
  SectionTitle,
} from './CentralResultados.style'

const OPCOES_FASE = FASES.map(f => ({ value: f, label: rotuloFase[f] }))

export function CentralResultados() {
  const navigate = useNavigate()
  const {
    ranking,
    isLoading,
    erro,
    competencia,
    competenciaComparacao,
    fases,
    recalculando,
    isExportando,
    exportar,
    onChangeCompetencia,
    onChangeCompetenciaComparacao,
    onChangeFases,
    recalcular,
  } = useCentralResultados()

  // Calculado por render, e nao no escopo do modulo: uma aba aberta atravessando a virada do mes
  // ficaria sem a competencia nova, e discordaria de competenciaCorrente(), que e avaliada sempre.
  const opcoesCompetencia = competenciasDisponiveis(24).map(c => ({
    value: c,
    label: formatCompetencia(c),
  }))

  const itens = ranking?.itens ?? []

  const colunas: ColumnsType<CarteiraRankingItemType> = [
    {
      title: 'Conta',
      dataIndex: 'nomeOrganizacao',
      sorter: (a, b) => a.nomeOrganizacao.localeCompare(b.nomeOrganizacao),
      render: (_, r) => (
        <>
          <div>{r.nomeOrganizacao}</div>
          <MetricFracao>{r.tenantId}</MetricFracao>
          {r.ativo === false ? (
            <>
              {' '}
              <Badge alerta>Desativada</Badge>
            </>
          ) : null}
        </>
      ),
    },
    {
      title: 'Fase',
      dataIndex: 'fase',
      render: (_, r) => (
        <Tag color={colors.secondary}>{rotuloFase[r.fase]}</Tag>
      ),
      sorter: (a, b) => a.fase.localeCompare(b.fase),
    },
    {
      title: 'Receita da carteira',
      // Nullable vai para -1 na ordenacao: sem dado nao disputa posicao com receita real.
      sorter: (a, b) =>
        (a.atual?.receitaTotal ?? -1) - (b.atual?.receitaTotal ?? -1),
      render: (_, r) => {
        if (!r.atual) return SEM_DADO
        const cobertura = rotuloCobertura(r.atual)
        return (
          <>
            <div>{formatMoeda(r.atual.receitaTotal)}</div>
            {/* Sem este rotulo, escolher o mes corrente (ou um mes ainda nao fechado) mostrava
                receita PARCIAL com cara de mes inteiro, em todas as contas de uma vez. O default
                da API evita o caso comum, mas o seletor permite chegar la de proposito. */}
            {cobertura ? (
              <Badge alerta={!r.atual.fechada}>{cobertura}</Badge>
            ) : null}
            {r.variacaoReceitaPercentual !== null ? (
              <DeltaHint
                positivo={r.variacaoReceitaPercentual > 0}
                negativo={r.variacaoReceitaPercentual < 0}
              >
                {formatVariacao(r.variacaoReceitaPercentual, '%')} vs{' '}
                {formatCompetencia(r.competenciaComparacao)}
              </DeltaHint>
            ) : null}
          </>
        )
      },
    },
    {
      title: 'Positivação',
      sorter: (a, b) =>
        (a.atual?.positivacaoPercentual ?? -1) -
        (b.atual?.positivacaoPercentual ?? -1),
      render: (_, r) => {
        if (!r.atual) return SEM_DADO
        const motivo = motivoSemPositivacao(r.atual)
        if (motivo) {
          return (
            <Tooltip title={motivo}>
              <span>
                <Badge alerta={r.atual.semMovimentoNaCompetencia}>
                  {rotuloSemPositivacao(r.atual)}
                </Badge>
              </span>
            </Tooltip>
          )
        }
        return (
          <>
            <div>{formatPercentual(r.atual.positivacaoPercentual)}</div>
            {/* Numerador e denominador SEMPRE ao lado do percentual. */}
            <MetricFracao>
              {formatNumero(r.atual.clientesAtivos)} de{' '}
              {formatNumero(r.atual.clientesElegiveis)} elegíveis
            </MetricFracao>
            {r.variacaoPositivacaoPontos !== null ? (
              <DeltaHint
                positivo={r.variacaoPositivacaoPontos > 0}
                negativo={r.variacaoPositivacaoPontos < 0}
              >
                {formatVariacao(r.variacaoPositivacaoPontos, ' p.p.')}
              </DeltaHint>
            ) : null}
          </>
        )
      },
    },
    {
      title: (
        <Tooltip title={AVISO_INDETERMINADO}>
          <span>Indeterminados</span>
        </Tooltip>
      ),
      sorter: (a, b) =>
        (a.atual?.clientesIndeterminados ?? -1) -
        (b.atual?.clientesIndeterminados ?? -1),
      render: (_, r) =>
        r.atual ? formatNumero(r.atual.clientesIndeterminados) : SEM_DADO,
    },
    {
      title: (
        <Tooltip title={AVISO_VALOR_MEDIO}>
          <span>Valor médio por cliente ativo</span>
        </Tooltip>
      ),
      sorter: (a, b) =>
        (a.atual?.valorMedioPorClienteAtivo ?? -1) -
        (b.atual?.valorMedioPorClienteAtivo ?? -1),
      render: (_, r) =>
        r.atual ? formatMoeda(r.atual.valorMedioPorClienteAtivo) : SEM_DADO,
    },
    {
      title: 'Ciclo de compra',
      sorter: (a, b) =>
        (a.intervaloMedianoCompras ?? -1) - (b.intervaloMedianoCompras ?? -1),
      render: (_, r) =>
        r.intervaloMedianoCompras === null
          ? SEM_DADO
          : `${formatDecimal(r.intervaloMedianoCompras)} dias`,
    },
    {
      title: 'Apurado em',
      // Carimbo POR TENANT: o "Atualizado em" do cabecalho e o MAX global e continuaria dizendo
      // "hoje" mesmo com o rollup de uma conta falhando ha tres noites.
      sorter: (a, b) => (a.apuradoEm ?? '').localeCompare(b.apuradoEm ?? ''),
      render: (_, r) => <Carimbo>{formatDataHora(r.apuradoEm)}</Carimbo>,
    },
  ]

  return (
    <Container>
      <ToastContainer theme="dark" />
      <ContainerTitle>
        <DivTitle color={colors.primaryText} size={24}>
          Central de Resultados
        </DivTitle>
      </ContainerTitle>

      <AvisoCausalidade>{AVISO_CAUSALIDADE}</AvisoCausalidade>

      <HeaderLinha>
        <Carimbo>
          {ranking?.competencia
            ? `Competência ${formatCompetencia(ranking.competencia)}`
            : 'Nenhuma competência congelada ainda'}
          {' · '}
          Atualizado em {formatDataHora(ranking?.atualizadoEm ?? null)}
        </Carimbo>
        <LinhaBotoes>
          {/* Exporta o conjunto INTEIRO do recorte, não a ordenação da tela: o arquivo é a prova
              do conjunto. Desabilitado sem linha — arquivo vazio não prova nada.

              `isLoading` também entra no disabled: durante um refetch a tabela ainda mostra a
              competência ANTERIOR enquanto os filtros já são os novos, e exportar nessa janela
              geraria um arquivo que discorda da tela — a única forma de quebrar a promessa acima. */}
          <Button
            onClick={exportar}
            loading={isExportando}
            disabled={isExportando || isLoading || itens.length === 0}
          >
            {isExportando ? 'Gerando…' : 'Exportar XLSX'}
          </Button>
          <Button onClick={recalcular} loading={recalculando} type="primary">
            {recalculando ? 'Recalculando…' : 'Recalcular agora'}
          </Button>
        </LinhaBotoes>
      </HeaderLinha>

      <FilterBar>
        <FilterField>
          <span>Competência</span>
          <Select
            style={{ width: 180 }}
            value={competencia || undefined}
            onChange={onChangeCompetencia}
            options={opcoesCompetencia}
            placeholder="Último mês fechado"
            allowClear
          />
        </FilterField>
        <FilterField>
          <span>Comparar com</span>
          <Select
            style={{ width: 180 }}
            value={competenciaComparacao || undefined}
            onChange={onChangeCompetenciaComparacao}
            options={opcoesCompetencia}
            placeholder="Sem comparação"
            allowClear
          />
        </FilterField>
        <FilterField>
          <span>Fase</span>
          <Select
            mode="multiple"
            style={{ minWidth: 260 }}
            value={fases}
            onChange={onChangeFases}
            options={OPCOES_FASE}
          />
        </FilterField>
      </FilterBar>

      {erro ? <AvisoBloco erro>{erro}</AvisoBloco> : null}

      {isLoading ? (
        <Loading contrast={false} />
      ) : (
        <Table
          rowKey="tenantId"
          columns={colunas}
          dataSource={itens}
          pagination={false}
          scroll={{ x: true }}
          locale={{
            // Com erro, "nenhuma conta" seria mentira: nao houve resposta para saber.
            emptyText: erro
              ? 'Não foi possível carregar o ranking.'
              : 'Nenhuma conta na fase selecionada.',
          }}
          onRow={record => ({
            onClick: () =>
              navigate(`/adm-central-resultados/${record.tenantId}`),
            style: { cursor: 'pointer' },
          })}
        />
      )}

      <SectionTitle>Como estes números são calculados</SectionTitle>
      <PainelConvencoes>
        <dl>
          <dt>Competência</dt>
          <dd>
            Mês-calendário. Toda comparação é competência × competência — nunca
            períodos de tamanhos diferentes.
          </dd>

          <dt>Cliente ativo (positivado)</dt>
          <dd>Ao menos uma compra com data no mês da competência.</dd>

          <dt>Inatividade</dt>
          <dd>
            90 dias corridos sem nenhuma compra, na data de referência da
            competência.
          </dd>

          <dt>Indeterminado</dt>
          <dd>{AVISO_INDETERMINADO}</dd>

          <dt>Positivação</dt>
          <dd>
            Clientes ativos ÷ (base − indeterminados). Quando não há base para
            afirmar ausência de compra, a taxa <strong>não é exibida</strong> —
            esta tela nunca mostra 0% no lugar de &quot;não sei&quot;.
          </dd>

          <dt>Receita</dt>
          <dd>{AVISO_RECEITA}</dd>

          <dt>Valor médio por cliente ativo</dt>
          <dd>{AVISO_VALOR_MEDIO}</dd>

          <dt>Ciclo de compra</dt>
          <dd>
            Intervalo mediano entre compras da carteira. É característica{' '}
            <strong>atual</strong> do cliente, não da competência — por isso não
            muda quando você troca o mês.
          </dd>
        </dl>
      </PainelConvencoes>
    </Container>
  )
}
